/* Client-side image compression — port of src/lib/compress-image.ts.
 * Resizes to max 2000px and compresses to JPEG under 1MB.
 *
 * HEIC handling (iPhone photos):
 *   - iOS Safari / iOS Chrome decode HEIC natively via WebKit → existing canvas
 *     path works. We force HEIC files through conversion so the server never
 *     receives a HEIC blob (the team's tooling expects JPEG/PNG).
 *   - Desktop Chrome/Firefox can't decode HEIC → we lazy-load the heic2any
 *     polyfill from jsdelivr only when the canvas path fails on a HEIC file.
 *     Polyfill is ~600KB but only paid for by users who actually upload HEIC.
 */

(function (global) {
  'use strict';
  var MAX_DIMENSION = 2000;
  var MAX_FILE_SIZE = 1 * 1024 * 1024;
  var INITIAL_QUALITY = 0.85;
  var MIN_QUALITY = 0.5;
  var HEIC_POLYFILL_URL = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';

  /* True if the file looks like HEIC/HEIF by MIME type or extension.
   * (Some Android browsers leave file.type empty for HEIC, so we also
   * sniff the extension.) */
  function isHeic(file) {
    var type = (file.type || '').toLowerCase();
    if (type === 'image/heic' || type === 'image/heif') return true;
    var name = (file.name || '').toLowerCase();
    return name.endsWith('.heic') || name.endsWith('.heif');
  }

  function loadImage(file) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      var url = URL.createObjectURL(file);
      img.onload = function () {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = function (err) {
        URL.revokeObjectURL(url);
        reject(err);
      };
      img.src = url;
    });
  }

  function canvasToBlob(canvas, type, quality) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(
        function (blob) { blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')); },
        type, quality
      );
    });
  }

  /* Lazy-load heic2any from jsdelivr. Cached after first call. */
  var heicScriptPromise = null;
  function ensureHeicPolyfill() {
    if (global.heic2any) return Promise.resolve();
    if (!heicScriptPromise) {
      heicScriptPromise = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = HEIC_POLYFILL_URL;
        s.async = true;
        s.onload = function () {
          global.heic2any ? resolve() : reject(new Error('heic2any failed to initialize'));
        };
        s.onerror = function () { reject(new Error('Failed to load HEIC converter')); };
        document.head.appendChild(s);
      });
    }
    return heicScriptPromise;
  }

  /* Convert HEIC blob → JPEG File using the polyfill. */
  async function heicToJpegViaPolyfill(file) {
    await ensureHeicPolyfill();
    var jpegBlob = await global.heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
    /* heic2any may return Blob[] when the HEIC has multiple images — take first. */
    if (Array.isArray(jpegBlob)) jpegBlob = jpegBlob[0];
    var baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
    return new File([jpegBlob], baseName + '.jpg', { type: 'image/jpeg' });
  }

  async function compressImage(file) {
    /* Reject non-images that aren't HEIC (HEIC sometimes has empty MIME). */
    var heic = isHeic(file);
    if (!heic && !(file.type || '').startsWith('image/')) return file;

    /* Fast path: small JPEG/PNG with reasonable dimensions — skip re-encode.
     * HEIC files always go through conversion below so the server gets JPEG. */
    if (!heic && file.size <= MAX_FILE_SIZE) {
      try {
        var small = await loadImage(file);
        if (small.width <= MAX_DIMENSION && small.height <= MAX_DIMENSION) return file;
      } catch (e) { /* fall through to canvas path */ }
    }

    /* Try native canvas decode first — works on Safari/iOS for HEIC, and on
     * any browser for JPEG/PNG/WebP. */
    var img;
    var workingFile = file;
    try {
      img = await loadImage(workingFile);
    } catch (err) {
      if (!heic) throw err;
      /* Desktop Chrome/Firefox can't decode HEIC — fall back to polyfill. */
      try {
        workingFile = await heicToJpegViaPolyfill(file);
        img = await loadImage(workingFile);
      } catch (polyErr) {
        var msg = (polyErr && polyErr.message) || 'HEIC conversion failed';
        throw new Error('Could not read iPhone photo (' + msg + '). Try sharing the photo via email first to convert it, or change Camera → Formats → Most Compatible in iPhone Settings.');
      }
    }

    var width = img.width;
    var height = img.height;
    if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
      var ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }

    var canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    var ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not supported');
    ctx.drawImage(img, 0, 0, width, height);

    var quality = INITIAL_QUALITY;
    var blob = null;
    while (quality >= MIN_QUALITY) {
      blob = await canvasToBlob(canvas, 'image/jpeg', quality);
      if (blob.size <= MAX_FILE_SIZE) break;
      quality -= 0.1;
    }
    if (!blob) throw new Error('Failed to compress image');

    var baseName = (workingFile.name || file.name || 'photo').replace(/\.[^.]+$/, '');
    return new File([blob], baseName + '.jpg', { type: 'image/jpeg' });
  }

  global.picCompressImage = compressImage;
})(window);
